import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import DashboardIcon from "@mui/icons-material/Dashboard";
import DevicesOtherIcon from "@mui/icons-material/DevicesOther";
import BoltIcon from "@mui/icons-material/Bolt";
import GroupIcon from "@mui/icons-material/Group";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Routes from "../../../inc/Routes";
import Permission from "../../../inc/Permission";
import { setMenuGroupState } from "../../../app/reducers/app/actions";
import "./styles.css";

// Пункты меню панели: только разделы, существующие в API (см. /API.md), сгруппированные по
// смыслу — плоский список 2026-07 (9+ разделов, из них сгруппировано только 2) на глаз читался
// как каша. Видимость групп/детей — по праву admin.<name>.index (группа видна, если виден
// хотя бы один child).
const MENU_ITEMS = [
    { name: 'dashboard', label: 'Дашборд', Icon: DashboardIcon },
    {
        // Всё про сам парк — ноды и то, что на них крутится.
        name: 'farm-group', label: 'Ферма', Icon: DevicesOtherIcon, children: [
            { name: 'nodes', label: 'Ноды' },
            // Из меню — сразу только работающие: /devices?status=active. Фильтр — обычный, в URL:
            // «Сбросить» в списке показывает все устройства.
            { name: 'devices', label: 'Устройства', search: '?status=active' },
            { name: 'device-types', label: 'Типы устройств' },
            { name: 'proxies', label: 'Прокси' },
            // Группы прокси — права те же admin.proxies.*.
            { name: 'proxy-groups', label: 'Группы прокси', permissionName: 'proxies' },
            // device-installs гейтится тем же admin.nodes (бэкенд: POST nodes/{id}/deploy
            // тоже под admin.nodes.edit) — своей отдельной permission-сущности у неё нет.
            { name: 'device-installs', label: 'Установка устройств', permissionName: 'nodes' },
        ],
    },
    {
        // Что сейчас происходит и куда смотреть, если что-то не так.
        name: 'ops-group', label: 'Операции', Icon: BoltIcon, children: [
            { name: 'scripts', label: 'Скрипты' },
            { name: 'files', label: 'APK-файлы' },
            // monitoring гейтится admin.devices (данные — device_performances конкретных
            // устройств), своей permission-сущности нет.
            { name: 'monitoring', label: 'Мониторинг', permissionName: 'devices' },
            // benchmarks гейтится admin.devices (данные — бенчмарки конкретных устройств),
            // своей permission-сущности нет — тот же приём, что monitoring.
            { name: 'benchmarks', label: 'Бенчмарки', permissionName: 'devices' },
            // screenshots гейтится admin.devices (снимки конкретных устройств) — тот же приём.
            { name: 'screenshots', label: 'Скриншоты', permissionName: 'devices' },
            { name: 'tasks', label: 'Задачи' },
            { name: 'logs', label: 'Логи' },
        ],
    },
    {
        name: 'users-group', label: 'Пользователи', Icon: GroupIcon, children: [
            { name: 'users', label: 'Пользователи' },
            { name: 'roles', label: 'Роли' },
        ],
    },
];

export default function Menu() {
    const dispatch = useDispatch();
    const location = useLocation();
    // Подписка на права — чтобы меню пересобралось при их смене (логин/логаут).
    // eslint-disable-next-line no-unused-vars
    const userPermissions = useSelector((state) => state.app.userPermissions);
    const menuGroupsState = useSelector((state) => state.app.menuGroupsState);

    // Через Permission.Check, а не напрямую по списку — при выключенном гейте видно всё.
    const isVisible = (item) => Permission.Check(`admin.${item.permissionName ?? item.name}.index`);

    return <nav className="menu">
        {MENU_ITEMS.map((item) => {
            if (item.children) {
                const visibleChildren = item.children.filter((child) => isVisible(child));
                if (visibleChildren.length === 0) return null;

                const hasActiveChild = visibleChildren.some(
                    (child) => location.pathname.startsWith(Routes.parseName(`${child.name}.index`)),
                );
                const isOpen = menuGroupsState[item.name] ?? hasActiveChild ?? true;

                return <div key={item.name} className="menu__group">
                    <button
                        type="button"
                        className="menu__item menu__item_group"
                        onClick={() => dispatch(setMenuGroupState(item.name, !isOpen))}
                    >
                        <item.Icon className="menu__item-icon" fontSize="small"/>
                        <span className="menu__item-label">{item.label}</span>
                        <ExpandMoreIcon className={`menu__group-arrow${isOpen ? ' menu__group-arrow_open' : ''}`} fontSize="small"/>
                    </button>
                    {isOpen ? <div className="menu__group-children">
                        {visibleChildren.map((child) =>
                            <NavLink
                                key={child.name}
                                to={`${Routes.parseName(`${child.name}.index`)}${child.search ?? ''}`}
                                className={({ isActive }) => `menu__item menu__item_child${isActive ? ' menu__item_active' : ''}`}
                            >
                                <span className="menu__item-label">{child.label}</span>
                            </NavLink>
                        )}
                    </div> : null}
                </div>;
            }

            if (!isVisible(item)) return null;

            return <NavLink
                key={item.name}
                to={`${Routes.parseName(`${item.name}.index`)}${item.search ?? ''}`}
                className={({ isActive }) => `menu__item${isActive ? ' menu__item_active' : ''}`}
            >
                <item.Icon className="menu__item-icon" fontSize="small"/>
                <span className="menu__item-label">{item.label}</span>
            </NavLink>;
        })}
    </nav>;
}
